import { supabase } from '../lib/supabase';

export const profileService = {
  async getProfile(userId) {
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) throw error;
    return data;
  },

  async getProviderProfile(userId) {
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('service_provider_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  async upsertProviderProfile(userId, payload) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('service_provider_profiles')
      .upsert({ user_id: userId, ...payload, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  toSnake(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    return Object.keys(obj).reduce((acc, k) => {
      const snake = k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
      acc[snake] = obj[k];
      return acc;
    }, {});
  },

  async updateOnboardingProfile(userId, payload) {
    if (!supabase) throw new Error('Supabase not configured');
    const snake = this.toSnake(payload);
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ ...snake, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async setProfileRecompleted(userId, payload) {
    if (!supabase) throw new Error('Supabase not configured');
    const snake = this.toSnake(payload);
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        ...snake,
        profile_recompleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async setOnboardingCompleted(userId, payload) {
    if (!supabase) throw new Error('Supabase not configured');
    const snake = this.toSnake(payload);
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        ...snake,
        onboarding_completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateProfileMedia(userId, payload) {
    if (!supabase) throw new Error('Supabase not configured');
    const updates = { updated_at: new Date().toISOString() };
    if (payload.avatar_url !== undefined) updates.avatar_url = payload.avatar_url;
    if (payload.banner_url !== undefined) updates.banner_url = payload.banner_url;
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

export const verificationDocumentsService = {
  async listMy(userId) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('verification_documents')
      .select('id, document_type, file_path, status, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async create(userId, documentType, filePath) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('verification_documents')
      .insert({ user_id: userId, document_type: documentType, file_path: filePath, status: 'pending' })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

export const favouritesService = {
  async list(userId) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('favourites')
      .select(`
        id,
        provider_id,
        user_profiles!favourites_provider_id_fkey(id, display_name, username, avatar_url, city)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async add(userId, providerId) {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase.from('favourites').insert({ user_id: userId, provider_id: providerId });
    if (error) throw error;
  },

  async remove(userId, providerId) {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('favourites')
      .delete()
      .eq('user_id', userId)
      .eq('provider_id', providerId);
    if (error) throw error;
  },

  async isFavourite(userId, providerId) {
    if (!supabase) return false;
    const { data } = await supabase
      .from('favourites')
      .select('id')
      .eq('user_id', userId)
      .eq('provider_id', providerId)
      .maybeSingle();
    return !!data;
  },
};

export const plansService = {
  async list() {
    if (!supabase) return [];
    const { data, error } = await supabase.from('plans').select('*').order('amount_paise');
    if (error) return [];
    return data || [];
  },
};

export const reviewsService = {
  async listForProvider(providerId) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('reviews')
      .select(`
        id, reviewer_id, rating, comment, created_at,
        user_profiles!reviews_reviewer_id_fkey(id, display_name, username)
      `)
      .eq('provider_id', providerId)
      .order('created_at', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async add(reviewerId, providerId, rating, comment = null) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('reviews')
      .insert({ reviewer_id: reviewerId, provider_id: providerId, rating, comment })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

export const chatService = {
  async listConversations(userId) {
    if (!supabase) return [];
    const { data: convos, error } = await supabase
      .from('conversations')
      .select('id, participant_one_id, participant_two_id, last_message_at')
      .or(`participant_one_id.eq.${userId},participant_two_id.eq.${userId}`)
      .order('last_message_at', { ascending: false });
    if (error) return [];
    const otherIds = (convos || []).map((c) =>
      c.participant_one_id === userId ? c.participant_two_id : c.participant_one_id
    );
    if (otherIds.length === 0) return [];
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, display_name, username, avatar_url')
      .in('id', otherIds);
    const byId = (profiles || []).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
    return (convos || []).map((c) => {
      const otherId = c.participant_one_id === userId ? c.participant_two_id : c.participant_one_id;
      return { ...c, other_id: otherId, other_profile: byId[otherId] };
    });
  },

  async getOrCreateConversation(userId, otherUserId) {
    if (!supabase) throw new Error('Supabase not configured');
    const [p1, p2] = [userId, otherUserId].sort();
    let { data, error } = await supabase
      .from('conversations')
      .select('id')
      .eq('participant_one_id', p1)
      .eq('participant_two_id', p2)
      .maybeSingle();
    if (error) throw error;
    if (data) return data.id;
    const { data: inserted, error: insertErr } = await supabase
      .from('conversations')
      .insert({ participant_one_id: p1, participant_two_id: p2 })
      .select('id')
      .single();
    if (insertErr) throw insertErr;
    return inserted.id;
  },

  async listMessages(conversationId) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('messages')
      .select('id, sender_id, receiver_id, message_text, created_at, read_at')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    if (error) return [];
    return data || [];
  },

  async sendMessage(conversationId, senderId, receiverId, text) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: senderId,
        receiver_id: receiverId,
        message_text: text,
      })
      .select('id, created_at')
      .single();
    if (error) throw error;
    await supabase
      .from('conversations')
      .update({ last_message_at: new Date().toISOString() })
      .eq('id', conversationId);
    return data;
  },
};

export const bookingsService = {
  async listForUser(userId) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        id, slot, status, amount_paise, created_at,
        user_profiles!bookings_provider_id_fkey(id, display_name, username, avatar_url)
      `)
      .eq('user_id', userId)
      .order('slot', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async listForProvider(providerId) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        id, slot, status, amount_paise, created_at,
        user_profiles!bookings_user_id_fkey(id, display_name, username, avatar_url)
      `)
      .eq('provider_id', providerId)
      .order('slot', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async create(userId, providerId, slot, amountPaise = null) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('bookings')
      .insert({
        user_id: userId,
        provider_id: providerId,
        slot,
        status: 'pending',
        amount_paise: amountPaise,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};
